// ============================================================================
// Weidner Oasis fork of the base oasis HomeNeighborhood.
//
// Sole delta vs the base: the "See The Neighborhood" CTA (`.hn-cta`) adopts
// the Header's "Schedule a Tour" pill COLORS (`.header-nav-cta` in
// `Header.tsx`) — PRIMARY fill / text-on-primary at rest swapping to
// SECONDARY on hover. The base swapped the other way (secondary → primary).
// The section's own heading font and uppercase label are kept as-is — only
// the colors change. All structure, tokens, slots, and the schema are
// unchanged from the base.
// ============================================================================
import { useEffect, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useCollectionBinding,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
// Reuses the same gold diamond pattern that anchors the Footer and
// PropertyOverview compositions — keeps the neighborhood section feeling
// like part of the same brand family rather than a one-off decoration.
import footerPatternUrl from '@vibesites/theme-oasis/assets/footer-pattern.png';

// ============================================================================
// Slot Group / Collection Definitions
// ----------------------------------------------------------------------------
// The left column is a single large square slideshow that auto-cycles
// through the property's `photo_gallery` (intended to be populated with
// neighborhood / surrounding-area imagery), sitting on top of a gold
// diamond pattern tucked into the bottom-right corner. The slideshow +
// pattern composition is a direct port of PropertyOverview's right
// column so the two home-page sections read as a matched pair.
// ============================================================================

// "Address" displays the property's full address as a clickable link to
// Google Maps directions. The default URL points to the neighborhood page
// so it stays useful even when the property has no address yet.
const addressLinkDef: SlotGroupDefinition = {
  label: 'Address Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/neighborhood' },
    label: { label: 'Address Text', type: 'text', defaultField: 'property_full_address' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// "See The Neighborhood" CTA — sits just below the address row in the
// right column. Mirrors PropertyOverview's `cta_link` shape (url + label
// + _target) so both home-page sections expose the same editor surface
// for their primary action button.
const ctaLinkDef: SlotGroupDefinition = {
  label: 'See The Neighborhood CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/neighborhood' },
    label: { label: 'Label', type: 'text', defaultValue: 'See The Neighborhood' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

interface GalleryPhoto {
  image?: string | null;
  original_url?: string | null;
  alt_text?: string | null;
  title?: string | null;
}

// Auto-advance interval for the slideshow. Matches PropertyOverview's
// 5s cadence so the two home-page slideshows pulse in unison rather
// than reading as competing carousels.
const SLIDE_INTERVAL_MS = 5000;

// ============================================================================
// Main Component
// ============================================================================

export function HomeNeighborhood() {
  const address = useSlotGroup('homeneighborhood.address', addressLinkDef);
  const ctaLink = useSlotGroup('homeneighborhood.cta_link', ctaLinkDef);
  const photos = useCollectionBinding<GalleryPhoto>('homeneighborhood.gallery', {
    collection: 'photo_gallery',
    defaultLimit: 8,
  });

  useRevealStyles();
  const contentRef = useScrollReveal<HTMLDivElement>();

  const total = photos.length;
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  // Normalize the (possibly negative / overflowing) raw index into the
  // valid range so prev/next can be unbounded increments.
  const safeIndex = total > 0 ? ((activeIndex % total) + total) % total : 0;

  useEffect(() => {
    if (total <= 1 || paused) return;
    const id = window.setInterval(() => {
      setActiveIndex((i) => i + 1);
    }, SLIDE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [total, paused]);

  const goPrev = () => setActiveIndex((i) => i - 1);
  const goNext = () => setActiveIndex((i) => i + 1);

  return (
    // Overflow strategy mirrors PropertyOverview: clip on the X axis,
    // allow Y so things like focus rings / vertical motion can render.
    // The diamond pattern stays inside its own stage wrapper so it
    // doesn't actually bleed past the section, but the accent square
    // overhang on the right does — `overflow-x-clip` traps that
    // horizontal bleed at narrow viewports without forcing a scroll
    // container the way plain `overflow-hidden` could under future
    // ancestor changes.
    <section className="home-neighborhood relative w-full overflow-x-clip bg-[var(--vibe-color-background,#fff)] pt-8 pb-10 md:pt-12 md:pb-16">
      <div
        ref={contentRef}
        className="reveal-fade-up max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start"
      >
        {/* ---- Left column: square slideshow on a diamond-pattern backdrop ----
            Layer order (back → front):
              1. Diamond gold pattern (CSS background-image) — tucked
                 into the bottom-LEFT corner of the wrapper so it peeks
                 out past the lower-LEFT edges of the slideshow. This
                 is a MIRROR of PropertyOverview's bottom-right L-shape
                 — the two home-page sections share the same motif but
                 cross each other diagonally on the page so they read
                 as a deliberate pair rather than a repeat. Uses the
                 SAME asset the Footer and PropertyOverview render so
                 the brand motif stays consistent.
              2. Single square slideshow — anchored TOP-RIGHT of the
                 stage (mirrors PropertyOverview's top-left anchoring)
                 so the bottom + left peek of the pattern lines up
                 with the slideshow's bottom + left edges. Cycles
                 through `photo_gallery` (intended for neighborhood
                 imagery) with crossfade + prev/next arrows.
        */}
        <div className="hn-photo-stage">
          {/* Layer 1 — diamond pattern (decorative backdrop) */}
          <div className="hn-photo-pattern" aria-hidden="true" role="presentation" />

          {/* Layer 2 — square slideshow.
              Positioned at TOP-RIGHT of the wrapper at 88%
              width/height so the diamond pattern peeks out from the
              bottom-LEFT corner as a decorative backdrop. The slide
              container itself is square (88% × 88% of an already-
              square parent), so each photo crops to a clean square
              regardless of the source aspect ratio.

              Auto-advance pauses while the user hovers (desktop) or
              focuses inside the slideshow (keyboard) — so a visitor
              inspecting a specific image isn't yanked off it. Pause
              is also implicit via the prev/next buttons, which
              directly drive `activeIndex`. */}
          <div
            className="hn-slideshow"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
            role="region"
            aria-roledescription="carousel"
            aria-label="Neighborhood gallery slideshow"
          >
            {total === 0 ? (
              <div className="hn-slideshow__empty">
                Neighborhood photos will appear here.
              </div>
            ) : (
              <>
                {photos.map((photo, i) => {
                  const src = photo.image ?? photo.original_url ?? '';
                  const alt = photo.alt_text ?? photo.title ?? 'Neighborhood photo';
                  const isActive = i === safeIndex;
                  return (
                    <div
                      key={`${src || 'slide'}-${i}`}
                      className={`hn-slideshow__slide${isActive ? ' is-active' : ''}`}
                      aria-hidden={isActive ? 'false' : 'true'}
                    >
                      {src ? (
                        <VibesitesImage
                          url={src}
                          alt={alt}
                          className="hn-slideshow__img"
                        />
                      ) : null}
                    </div>
                  );
                })}

                {total > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={goPrev}
                      className="hn-slideshow__arrow hn-slideshow__arrow--prev"
                      aria-label="Previous image"
                    >
                      &#8249;
                    </button>
                    <button
                      type="button"
                      onClick={goNext}
                      className="hn-slideshow__arrow hn-slideshow__arrow--next"
                      aria-label="Next image"
                    >
                      &#8250;
                    </button>
                  </>
                )}
              </>
            )}
          </div>

        </div>

        {/* ---- Right column: copy ---- */}
        <div className="hn-copy">
          <RichTextContent
            path="homeneighborhood.eyebrow"
            label="Pre-title"
            defaultValue='<p>Your Life in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>'
            as="p"
            className="hn-eyebrow"
          />

          <RichTextContent
            path="homeneighborhood.title"
            label="Title"
            defaultValue="<p>Explore Your Surroundings</p>"
            as="h2"
            className="hn-title"
          />

          {/* Description and address are tabbed in (md:ml-16) at md+
              to echo the indentation treatment used on the
              PropertyOverview section's blurb and CTA, so the two
              home-page sections share a consistent left-edge rhythm
              where the title hugs the column and the supporting copy
              sits inset from it. On mobile both drop the tab so the
              copy stack hugs the column's left edge — flush with the
              eyebrow and title above — which reads as a clean
              stacked column instead of a hanging indent. */}
          <RichTextContent
            path="homeneighborhood.description"
            label="Description"
            defaultValue="<p>Our community is in the perfect location — close to the area's best dining, shopping, entertainment, and outdoor escapes. Becoming every renter's perfect home, we sit at the heart of everything that makes this neighborhood feel like home.</p>"
            as="div"
            className="hn-description md:ml-16"
          />

          {/* Address pin link — pulls the property's full address and
              opens directions when clicked. Defaults to the
              /neighborhood page when no URL is supplied. */}
          <EditableSlotGroup
            groupPath="homeneighborhood.address"
            definition={addressLinkDef}
            className="hn-address-wrap md:ml-16"
          >
            <a
              href={address.url || '/neighborhood'}
              target={address._target || '_self'}
              className="hn-address"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="hn-address-pin"
                aria-hidden="true"
              >
                <path
                  d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22S19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z"
                  fill="currentColor"
                />
              </svg>
              <span className="hn-address-text">
                {address.label || 'View Neighborhood'}
              </span>
            </a>
          </EditableSlotGroup>

          {/* CTA — adopts the Header's "Schedule a Tour" pill COLORS
              (`.header-nav-cta` in `Header.tsx`): primary fill /
              text-on-primary at rest, swapping to secondary on hover. The
              section's own heading font + uppercase label are kept. Tabbed
              in to align with the description + address indentation. */}
          <EditableSlotGroup
            groupPath="homeneighborhood.cta_link"
            definition={ctaLinkDef}
            className="hn-cta-wrap md:ml-16"
          >
            <a
              href={ctaLink.url || '/neighborhood'}
              target={ctaLink._target || '_self'}
              className="hn-cta"
            >
              {ctaLink.label || 'See The Neighborhood'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        /* Tablet-only override: trim the section's bottom padding so
           the gap beneath the address (the last element in the copy
           column) feels tighter on tablet than the lg+ value. The
           base section uses Tailwind \`md:pb-16\` (4rem) which
           carries through to lg+ — by scoping this override to
           \`max-width: 1023.98px\` we leave desktop's roomier footer
           space alone. */
        @media (min-width: 768px) and (max-width: 1023.98px) {
          .home-neighborhood { padding-bottom: 2rem; }
        }

        /* ============================================================
           Left column — square slideshow on diamond-pattern backdrop.
           Geometry is a direct port of PropertyOverview's right-column
           composition (see \`PropertyOverview.tsx\`) so the two
           home-page slideshows read as a matched pair, with this
           section's HN-specific overlay (the home/heart accent square)
           layered on top of the same base composition.
           ============================================================ */
        .hn-photo-stage {
          position: relative;
          width: 100%;
          max-width: 640px;
          aspect-ratio: 1 / 1;
          margin: 0 auto;
        }
        @media (min-width: 1024px) {
          .hn-photo-stage { margin: 0; }
        }

        /* Diamond pattern decoration — sits BEHIND the square slideshow
           and forms an L-shape wrapping the bottom-LEFT corner
           (mirror of PropertyOverview's bottom-right L). Three
           offsets tune the visible peek:

             - \`left: 6%\`: the rectangle's left edge sits 6%
               inward from the stage's left edge. The slideshow's
               own left edge is at X 12%, so the LEFT band peeks
               from X 6% to X 12% — a 6%-wide bleed past the
               slideshow rather than the 12%-wide bleed you'd get
               with \`left: 0\`. Narrower bleed reads as a tight
               accent along the slideshow's left edge instead of a
               full stage-width bottom band.
             - \`top: 8%\`: the top edge of the rectangle sits near
               the top of the slideshow, so the left band runs
               nearly the full height of the slideshow's left edge.
             - \`bottom: 5%\`: the bottom edge of the rectangle stops
               5% above the stage floor. The slideshow's bottom is
               at Y 88%, so the BOTTOM band peeks from Y 88% to
               Y 95% — a 7%-tall strip just below the slideshow.

           Background-position is anchored to BOTTOM-LEFT (\`0 100%\`)
           so the visible tiles land in the bottom-left portion of
           the pattern rectangle (the part NOT covered by the
           slideshow). Same diamond asset and same 65% tile size as
           PropertyOverview so the individual diamond motif reads at
           one consistent scale across both home-page sections —
           only the rectangle's footprint changes. */
        .hn-photo-pattern {
          position: absolute;
          left: 6%;
          right: 7%;
          top: 8%;
          bottom: 5%;
          background-image: url(${footerPatternUrl});
          background-repeat: repeat;
          background-size: 65%;
          background-position: 0 100%;
          pointer-events: none;
          z-index: 0;
        }

        /* Square slideshow — single large image (88% × 88% of the
           square stage) anchored to the TOP-RIGHT of the composition
           so the diamond pattern peeks out behind the bottom-LEFT
           corner (mirror of PropertyOverview's top-left anchor). All
           slides are absolutely stacked at inset:0 and crossfaded via
           opacity, with the active slide on top of the inactive ones. */
        .hn-slideshow {
          position: absolute;
          top: 0;
          right: 0;
          width: 88%;
          height: 88%;
          overflow: hidden;
          background-color: #1f2937;
          z-index: 1;
        }

        /* Mobile: mirror of the PropertyOverview treatment — hide the
           decorative diamond peek and let the slideshow fill the
           centered square stage so the gallery reads as squarely
           centered in the column rather than offset to one corner.
           The md+ asymmetric composition (top-right slideshow,
           bottom-left pattern peek) is untouched. */
        @media (max-width: 767.98px) {
          .hn-photo-pattern { display: none; }
          .hn-slideshow {
            width: 100%;
            height: 100%;
            right: auto;
            left: 0;
          }
        }

        .hn-slideshow__empty {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(255, 255, 255, 0.7);
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          text-align: center;
          padding: 1rem;
        }

        .hn-slideshow__slide {
          position: absolute;
          inset: 0;
          opacity: 0;
          transition: opacity 0.9s ease-in-out;
          pointer-events: none;
        }

        .hn-slideshow__slide.is-active {
          opacity: 1;
          pointer-events: auto;
        }

        .hn-slideshow__img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        /* Prev/Next arrow buttons — bare white chevrons (no background
           pill) so the slideshow chrome stays minimal and lets the
           photo behind it read cleanly. A text-shadow keeps the
           chevrons legible against light areas of the underlying
           image. Edge-anchored at 50% Y so they sit centered against
           the square. */
        .hn-slideshow__arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          color: #fff;
          font-size: 2.5rem;
          line-height: 1;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 0.15rem 0.55rem;
          opacity: 0.9;
          text-shadow: 0 1px 6px rgba(0, 0, 0, 0.55);
          z-index: 2;
          -webkit-user-select: none;
          user-select: none;
          transition: opacity 0.15s, transform 0.15s;
        }
        .hn-slideshow__arrow:hover {
          opacity: 1;
        }
        .hn-slideshow__arrow:focus-visible {
          opacity: 1;
          outline: 2px solid var(--vibe-color-accent, #fff);
          outline-offset: 2px;
        }
        .hn-slideshow__arrow--prev { left: 0.5rem; }
        .hn-slideshow__arrow--next { right: 0.5rem; }

        @media (prefers-reduced-motion: reduce) {
          .hn-slideshow__slide {
            transition: none;
          }
        }

        /* ============================================================
           Right column — copy
           ============================================================ */
        .hn-copy {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
          max-width: 36rem;
        }
        @media (min-width: 1024px) {
          .hn-copy { padding-left: 1.5rem; padding-top: 1rem; }
        }

        .hn-eyebrow {
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          font-weight: 700;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--vibe-color-secondary);
          margin: 0;
        }
        /* Tablet-only top padding above the eyebrow. On lg+ the
           \`.hn-copy\` already gets \`padding-top: 1rem\`, which gives
           the column a small inset off the top of the photo. In the
           md range the copy column sits above the photo grid item
           with no such inset, so the eyebrow lands tight to the
           section's top padding. A small padding-top here gives the
           eyebrow a touch of breathing room on tablet without
           pushing the text far down the page (was \`1.5rem\` — pulled
           in tighter so the text rises up). */
        @media (min-width: 768px) and (max-width: 1023.98px) {
          .hn-eyebrow { padding-top: 0.5rem; }
        }

        /* Title sizing mirrors PropertyOverview's
           \`text-xl sm:text-2xl md:text-3xl\` Tailwind ladder + \`leading-tight\`
           so the two home-page sections share an identical headline scale.
           Color locked to \`--vibe-color-primary\` to match PropertyOverview's
           title treatment so both home-page headlines anchor with the same
           primary brand color. */
        .hn-title {
          font-family: var(--vibe-font-heading);
          font-size: 1.25rem;
          line-height: 1.25;
          font-weight: 700;
          color: var(--vibe-color-primary);
          margin: 0;
        }
        @media (min-width: 640px) {
          .hn-title { font-size: 1.5rem; }
        }
        @media (min-width: 768px) {
          .hn-title { font-size: 1.875rem; }
        }
        .hn-title p { margin: 0; }

        /* Description sizing mirrors PropertyOverview's \`text-base\`
           (1rem) so body copy across both home sections renders at the
           same scale. Line-height is the airy 1.92 the theme uses
           across prose to match
           https://www.lakehausapartmenthomes.com/ — the same value
           PropertyOverview's blurb uses via \`leading-[1.92]\`. */
        .hn-description {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.92;
          color: var(--vibe-color-text);
        }
        .hn-description p { margin: 0; }
        .hn-description p + p { margin-top: 0.85rem; }

        .hn-address-wrap { display: inline-flex; }

        /* Address text matches the description's \`text-sm\` (0.875rem)
           sizing so all body-level copy in the section reads at one
           consistent scale, aligned with PropertyOverview. */
        .hn-address {
          display: inline-flex;
          align-items: flex-start;
          gap: 0.65rem;
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--vibe-color-text);
          text-decoration: none;
          line-height: 1.4;
          transition: color 0.2s ease;
        }
        .hn-address:hover { color: var(--vibe-color-primary); }
        .hn-address-pin {
          width: 22px;
          height: 22px;
          color: var(--vibe-color-primary);
          flex-shrink: 0;
          margin-top: 1px;
        }
        .hn-address-text { display: inline-block; }

        /* ============================================================
           CTA — adopts the Header's \`.header-nav-cta\` COLORS (see
           \`Header.tsx\`): primary fill / text-on-primary label at rest,
           hover swaps to secondary fill / text-on-secondary and lifts
           1px. The section's own heading font + uppercase label, plus the
           shared 2px radius / 600 weight / 0.05em tracking, are kept — only
           the colors changed (the base swapped secondary → primary).
           \`.hn-cta-wrap\` uses inline-flex so the editable wrapper hugs
           the pill rather than stretching to the column width. A small
           top margin separates the CTA from the address row above it.
           ============================================================ */
        /* On mobile the wrapper goes block-level flex so it can center
           the inline pill within the column; at md+ it reverts to
           inline-flex so it hugs the pill the way it always did and
           the link's \`md:ml-16\` indent lines up with the description
           and address above. */
        .hn-cta-wrap {
          display: flex;
          justify-content: center;
          margin-top: 0.5rem;
        }
        @media (min-width: 768px) {
          .hn-cta-wrap {
            display: inline-flex;
            justify-content: flex-start;
          }
        }
        .hn-cta {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.875rem 2.5rem;
          background-color: var(--vibe-color-primary);
          border: none;
          border-radius: 2px;
          color: var(--vibe-color-text-on-primary);
          font-family: var(--vibe-font-heading);
          font-size: 1rem;
          font-weight: 600;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          text-decoration: none;
          white-space: nowrap;
          transition: background-color 0.25s ease, color 0.25s ease, transform 0.25s ease;
        }
        .hn-cta:hover {
          background-color: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary);
          transform: translateY(-1px);
        }
        .hn-cta:active {
          transform: translateY(0);
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'HomeNeighborhood',
  displayName: 'Home Neighborhood',
  tokens: {
    'homeneighborhood.eyebrow': {
      label: 'Pre-title',
      type: 'richtext',
      defaultValue:
        '<p>Your Life in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>',
    },
    'homeneighborhood.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Explore Your Surroundings</p>',
    },
    'homeneighborhood.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_home_neighborhood_blurb',
      defaultValue:
        '<p>Our community is in the perfect location — close to the area\'s best dining, shopping, entertainment, and outdoor escapes. Becoming every renter\'s perfect home, we sit at the heart of everything that makes this neighborhood feel like home.</p>',
    },
  },
  slotGroups: {
    'homeneighborhood.address': {
      label: 'Address Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/neighborhood' },
        label: { label: 'Address Text', type: 'text', defaultField: 'property_full_address' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'homeneighborhood.cta_link': {
      label: 'See The Neighborhood CTA',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/neighborhood' },
        label: { label: 'Label', type: 'text', defaultValue: 'See The Neighborhood' },
        _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
  collectionBindings: {
    'homeneighborhood.gallery': {
      label: 'Neighborhood Slideshow Photos',
      collection: 'photo_gallery',
      defaultLimit: 8,
      description:
        'Photos that cycle through the square slideshow on the left side of the neighborhood section. Pulled from the property photo gallery — pick the entries that best represent the surrounding area / cityscape so the slideshow reads as a neighborhood tour.',
    },
  },
};

(HomeNeighborhood as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default HomeNeighborhood;
